// FacilityContext — loads the facility row on app startup so every page can
// read the hotel's name, contact details, location and logo without having
// to call the backend itself.  The provider also tracks whether setup has
// been completed so route guards can redirect first-time visitors to /setup.
//
// Shape exposed by useFacility():
//   {
//     facility:   { id, name, email, phone, location, region, digital_address, logo } | null,
//     configured: boolean,   // true once a super_admin exists
//     loading:    boolean,   // true while the initial status check is in-flight
//     refresh:    () => Promise<void>,   // re-fetch (call after setup completes)
//   }
//
// Convenience getter:  helper that returns the first word of the facility
// name (the "highlighted" word the hero/branding panels italicise) along
// with the rest, so the original two-tone typography keeps working.

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { facilityStatus, facility as facilityUrl } from "../BaseURL";

const FacilityContext = createContext({
  facility: null,
  configured: false,
  loading: true,
  refresh: async () => {},
});

export function FacilityProvider({ children }) {
  const [facility, setFacility]     = useState(null);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading]       = useState(true);

  const refresh = useCallback(async () => {
    try {
      // /facilityStatus is the cheap call (id, name, logo) — pair it with
      // /facility to pick up the full contact details we splash around the
      // marketing site.
      const [statusRes, fullRes] = await Promise.all([
        axios.get(facilityStatus),
        axios.get(facilityUrl).catch(() => ({ data: { facility: null } })),
      ]);
      setConfigured(!!statusRes.data?.configured);
      setFacility(fullRes.data?.facility || statusRes.data?.facility || null);
    } catch (e) {
      console.error("FacilityContext refresh failed:", e);
      // Leave whatever we had — networks blip, the UI shouldn't blow up.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <FacilityContext.Provider value={{ facility, configured, loading, refresh }}>
      {children}
    </FacilityContext.Provider>
  );
}

export function useFacility() {
  return useContext(FacilityContext);
}

// Split "Bisong Guest House" into ["Bisong", "Guest House"] for the two-tone
// titles in the marketing pages.  Returns ["", ""] when the facility hasn't
// been loaded yet so callers can render placeholders safely.
export function splitFacilityName(name) {
  if (!name) return ["", ""];
  const trimmed = name.trim();
  const firstSpace = trimmed.indexOf(" ");
  if (firstSpace === -1) return [trimmed, ""];
  return [trimmed.slice(0, firstSpace), trimmed.slice(firstSpace + 1)];
}
